import { lazy } from 'react'
import type { FeatureCollection } from 'geojson'
import { useQueryColumns, useQueryData } from '@hooks/useQuery'
import {
  toCategoriesSeries,
  toPieSlices,
  toMapSlices
} from 'src/utils/chartTransformers'
import ChartSkeleton from '@components/skeletons/ChartSkeleton/ChartSkeleton'
import type { ChartType, ValueOverride } from 'src/types/dashboard'
import EmptyChartState from '../EmptyChartState/EmptyChartState'

const BarChart = lazy(() => import('@components/charts/BarChart'))
const LineChart = lazy(() => import('@components/charts/LineChart'))
const PieChart = lazy(() => import('@components/charts/PieChart'))
const MapChart = lazy(() => import('@components/charts/MapChart'))

interface ChartAreaProps {
  queryId: string
  chartType: ChartType
  xAxis: string[]
  yAxis: string[]
  geoJsonData?: FeatureCollection
  geoColumn?: string
  overrides?: Record<string, ValueOverride>
}

interface ChartAreaInnerProps {
  queryId: string
  chartType: ChartType
  xAxis: string[]
  yAxis: string[]
  geoJsonData?: FeatureCollection
  geoColumn?: string
  overrides?: Record<string, ValueOverride>
}

function ChartAreaInner({
  queryId,
  chartType,
  xAxis,
  yAxis,
  geoColumn,
  overrides
}: ChartAreaInnerProps) {
  const { data, colSpec } = useQueryData(queryId)

  if (!data || data.length === 0) {
    return <EmptyChartState />
  }

  if (chartType === 'bar-chart') {
    const { categories, series } = toCategoriesSeries(
      data,
      xAxis,
      yAxis,
      overrides
    )
    return (
      <BarChart categories={categories} series={series} initialZoomCount={50} />
    )
  }

  if (chartType === 'line-chart') {
    const { categories, series } = toCategoriesSeries(
      data,
      xAxis,
      yAxis,
      overrides
    )
    return (
      <LineChart
        categories={categories}
        series={series}
        area
        smooth
        initialZoomCount={150}
      />
    )
  }

  if (chartType === 'pie-chart') {
    const slices = toPieSlices(data, xAxis, yAxis, overrides)
    return <PieChart data={slices} donut />
  }

  if (chartType === 'map-chart') {
    const { slices, geoJsonData, customColorMap } = toMapSlices(
      data,
      xAxis,
      yAxis,
      colSpec,
      geoColumn,
      overrides
    )

    if (!geoJsonData || geoJsonData.features.length === 0) {
      return <EmptyChartState />
    }

    return (
      <MapChart
        geoJsonData={geoJsonData}
        data={slices}
        customColorMap={customColorMap}
      />
    )
  }

  return null
}

export default function ChartArea(props: ChartAreaProps) {
  if (!__CLIENT__) return <ChartSkeleton />
  return <ChartAreaInner {...props} />
}
